import * as startOfDay from 'date-fns/start_of_day';
import { action, computed, observable, runInAction } from 'mobx';

import { CURRENCY_ALIAS, CURRENCY_ID, DEFAULT_UNLINKED_ROOM_ID } from '../consts';
import {
    ActualizationData,
    ActualizationStatus,
    CurrencyId,
    HotelInStop,
    ResourceTypes,
    TourPriceStatus,
    TourService
} from '../models/actualization';
import { FlightsData, FlightsParsedData, FlightsStatus, FlightSeatsIndicator } from '../models/flights';
import { parseFlights } from '../utils/flights';
import { detailedActualizationService, DetailedActualizationService } from '../services/detailed-actualization';
import { CurrencyStore, getCurrencyRates } from '../services/get-currency-rates';
import { ExtendedTour } from '../services/get-tours';
import {
    BuyOnlineStatus,
    getBuyOnlineStatus,
    hasDepartTickets,
    hasNoDepartTickets,
    hasNoReturnTickets,
    hasReturnTickets,
    isTourSold
} from '../utils/availability';
import { parseDateString } from '../utils/parsers';
import { ActualizationInfo, primaryActualization } from '../services/primary-actualization';
import { hotelPageLogger } from '../../utils/logger';
import { defaultFlightPackage } from '../consts/flights';
import { MealType } from 'sletat-api-services/lib/sletat/models/initial-state/page-models/hotel';


export class TourStore {

    // https://agile.sletat.ru/jiraurl/browse/SLT-2466?focusedCommentId=187733&page=com.atlassian.jira.plugin.system.issuetabpanels:comment-tabpanel#comment-187733
    @observable
    isDuplicate = false;

    @observable
    isAddedToBasket = false;

    // текущая выбранная валюта, может переключаться в окошке доплат в правой колонке
    @observable
    selectedCurrencyId: CurrencyId = CURRENCY_ID;

    @observable
    currencyRates: CurrencyStore = new CurrencyStore(null);

    @observable
    readonly mealTypes: MealType[];

    @observable
    private _flightsInfo: FlightsParsedData | null = null;

    @observable
    private primaryActualizationStatus: ActualizationStatus = ActualizationStatus.Initial;

    @observable
    private detailedActualizationStatus: ActualizationStatus = ActualizationStatus.Initial;

    @observable
    private primaryActualizationData: ActualizationData | null = null;

    @observable
    private detailedActualizationData: ActualizationData | null = null;

    // данные полученные при поиске туров
    @observable
    private readonly searchData: ExtendedTour;

    private detailedService: DetailedActualizationService | null = null;


    constructor(tour: ExtendedTour, mealTypes?: MealType[]) {
        this.searchData = tour;
        this.mealTypes = mealTypes || [];
    }


    @computed
    get flightsInfo(): FlightsParsedData {
        const getStatus = (): FlightsStatus => {
            const tour = this.actualizationData
                ? this.actualizationData.tour
                : null;

            if (!this.isTourActualizedCompletely) {
                return FlightsStatus.Loading;
            }
            if (tour && !this.isTicketsIncluded) {
                return FlightsStatus.FlightsNotIncluded;
            }
            if (this.detailedActualizationStatus === ActualizationStatus.Failed) {
                return FlightsStatus.NotConcrete;
            }
            if (this.primaryActualizationData && !this.primaryActualizationData.isDetailedExists && this.isTicketsIncluded) {
                return FlightsStatus.NoData;
            }
            if (this.isSold) {
                return FlightsStatus.NoTickets;
            }
            if (!this._flightsInfo) {
                return FlightsStatus.NoData;
            }
            return this._flightsInfo.status;
        };

        const getFlightsInfo = (): FlightsData => {
            const tour = this.actualizationData
                ? this.actualizationData.tour
                : null;

            const hasNoFlightsInfo = !this._flightsInfo && tour;
            const flightsHaveNoData = this._flightsInfo && tour && (this._flightsInfo.status === FlightsStatus.NoData);

            if (hasNoFlightsInfo || flightsHaveNoData) {
                return defaultFlightPackage;
            }

            return this._flightsInfo || defaultFlightPackage;
        };

        return { ...getFlightsInfo(), status: getStatus() };
    }

    @computed
    get isTicketsIncluded(): boolean {
        return !!this.actualizationData && !!this.actualizationData.tour && this.actualizationData.tour.flightInfo.ticketsInPrice;
    }

    @computed
    get flightSeatsTo(): FlightSeatsIndicator {
        const tour = this.actualizationData ? this.actualizationData.tour : null;
        if (tour && hasDepartTickets(tour) && tour.flightInfo.isFewEconomyDepartTickets) {
            return FlightSeatsIndicator.Few;
        }

        const hasNoTicketsAtAll = this.isTicketsIncluded && this.flightsInfo.status === FlightsStatus.NoTickets;
        if (tour && hasNoDepartTickets(tour) || hasNoTicketsAtAll) {
            return FlightSeatsIndicator.None;
        }

        return FlightSeatsIndicator.Enough;
    }

    @computed
    get flightSeatsFrom(): FlightSeatsIndicator {
        const tour = this.actualizationData ? this.actualizationData.tour : null;
        if (tour && hasReturnTickets(tour) && tour.flightInfo.isFewEconomyReturnTickets) {
            return FlightSeatsIndicator.Few;
        }

        const hasNoTicketsAtAll = this.isTicketsIncluded && this.flightsInfo.status === FlightsStatus.NoTickets;
        if (tour && hasNoReturnTickets(tour) || hasNoTicketsAtAll) {
            return FlightSeatsIndicator.None;
        }

        return FlightSeatsIndicator.Enough;
    }

    @computed
    get rawFlightsData(): string[] {
        if (!this.actualizationData) {
            return [];
        }

        const onlyFlights = [ResourceTypes.flightFrom, ResourceTypes.flightTo, ResourceTypes.transferFlight];

        return this.actualizationData.resources
            .filter(r => onlyFlights.indexOf(r.type) > -1 && r.isChecked)
            .map(r => r.name);
    }

    @computed
    get isRequiredPrimaryActualization(): boolean {
        return this.primaryActualizationStatus === ActualizationStatus.Initial;
    }

    @computed
    get isRequiredDetailedActualization(): boolean {
        return this.detailedActualizationStatus === ActualizationStatus.Initial;
    }

    @computed
    get isTourActualizedCompletely(): boolean {
        return this.isPrimaryActualizationCompletely && this.isDetailedActualizationCompletely;
    }

    @computed
    get isPrimaryActualizationCompletely(): boolean {
        const notFound = this.primaryActualizationStatus === ActualizationStatus.TourNotFound;
        const success = this.primaryActualizationStatus === ActualizationStatus.Success;
        const failed = this.primaryActualizationStatus === ActualizationStatus.Failed;

        return notFound || success || failed;
    }

    @computed
    get isDetailedActualizationCompletely(): boolean {
        const detailedNotFound = this.detailedActualizationStatus === ActualizationStatus.TourNotFound;
        const detailedFailed = this.detailedActualizationStatus === ActualizationStatus.Failed;

        return detailedNotFound || this.isDetailedActualizationSuccess || detailedFailed;
    }

    @computed
    get isDetailedActualizationSuccess(): boolean {
        return this.detailedActualizationStatus === ActualizationStatus.Success;
    }

    @computed
    get isDetailedActualizationFailed(): boolean {
        return this.detailedActualizationStatus === ActualizationStatus.Failed;
    }

    @computed
    get isActualizationInProcess(): boolean {
        return this.detailedActualizationStatus === ActualizationStatus.InProcess ||
            this.primaryActualizationStatus === ActualizationStatus.InProcess;
    }

    @computed
    get priceStatus(): TourPriceStatus {
        // todo:crutch#1: remove it when actualization will be needed
        if ('true') {
            return TourPriceStatus.ExtraPay;
        }

        const tourNotExistsYet = !this.actualizationData || !this.actualizationData.tour;
        if (tourNotExistsYet || this.isActualizationInProcess) {
            return TourPriceStatus.Pending;
        }

        if (this.isQuickBooking) {
            return TourPriceStatus.QuickBooking;
        }

        if (this.shouldShowAsSold && this.isTourActualizedCompletely) {
            return TourPriceStatus.Sold;
        }

        if (this.buyOnlineStatus === BuyOnlineStatus.operatorDoesntSupportOnlinePayment) {
            return TourPriceStatus.NotSupportedOnlineBilling;
        }

        if (!this.shouldShowAsSold && this.isDetailedActualizationSuccess) {
            return TourPriceStatus.CheckedOnline;
        }

        if (!this.shouldShowAsSold && this.detailedActualizationStatus === ActualizationStatus.Failed) {
            return TourPriceStatus.ExtraPay;
        }

        return TourPriceStatus.Default;
    }

    @computed
    get buyOnlineStatus(): BuyOnlineStatus {
        if (this.shouldShowAsSold) {
            return BuyOnlineStatus.tourSold;
        }
        return getBuyOnlineStatus({
            sourceId: this.sourceId,
            tour: this.actualizationData!.tour!,
            resources: this.actualizationData!.resources,
            resourcesData: this.actualizationData!.resourceData,
            originalCurrency: this.actualizationData!.tour!.tourOperatorCurrencyId,
            operatorsWithBilling: [],
            isActualizationNotAvailable: !this.actualizationData!.isDetailedExists,
            detailActualizationError: this.detailedActualizationStatus === ActualizationStatus.Failed,
            detailActualizationCompleted: this.isDetailedActualizationCompletely
        });
    }

    @computed
    get shouldShowAsSold(): boolean {
        return this.isSold || this.isDepartDateInPast;
    }

    @computed
    get tourId(): string {
        return `#${this.sourceId}-${this.offerId}-${this.requestId}`;
    }

    @computed
    get hotelId(): number | null {
        return this.actualizationData && this.actualizationData.tour
            ? this.actualizationData.tour.hotel.id
            : null;
    }

    @computed
    get sourceId(): number {
        return this.searchData.sourceId;
    }

    @computed
    get offerId(): number {
        return this.searchData.offerId;
    }

    @computed
    get requestId(): number {
        return this.searchData.requestId;
    }

    @computed
    get tourHash(): string {
        return this.searchData.tourHash;
    }

    @computed
    get departDate(): Date | null {
        return parseDateString(this.departDateString);
    }

    @computed
    get arrivalDate(): Date | null {
        return (this.actualizationData && this.actualizationData.tour && this.actualizationData.tour.arrivalDate)
            ? parseDateString(this.actualizationData.tour.arrivalDate)
            : parseDateString(this.arrivalDateString);
    }

    @computed
    get departDateString(): string {
        return (this.actualizationData && this.actualizationData.tour && this.actualizationData.tour.departDate)
            ? this.actualizationData.tour.departDate
            : this.searchData.departDate;
    }

    @computed
    get arrivalDateString(): string {
        return (this.actualizationData && this.actualizationData.tour && this.actualizationData.tour.arrivalDate)
            ? this.actualizationData.tour.arrivalDate
            : this.searchData.arrivalDate;
    }

    @computed
    get nights(): number {
        return (this.actualizationData && this.actualizationData.tour)
            ? this.actualizationData.tour.nightsCount
            : this.searchData.nightsCount;
    }

    @computed
    get adults(): number | null {
        return (this.actualizationData && this.actualizationData.tour)
            ? this.actualizationData.tour.adultCount
            : null;
    }

    @computed
    get kids(): number | null {
        return (this.actualizationData && this.actualizationData.tour)
            ? this.actualizationData.tour.kidsCount
            : null;
    }

    @computed
    get cityName(): string {
        if (this.actualizationData && this.actualizationData.tour) {
            return this.actualizationData.tour.cityName;
        }
        return this.searchData.cityName;
    }

    @computed
    get countryName(): string {
        if (this.actualizationData && this.actualizationData.tour) {
            return this.actualizationData.tour.countryName;
        }
        return this.searchData.countryName;
    }

    @computed
    get resortName(): string {
        if (this.actualizationData && this.actualizationData.tour) {
            return this.actualizationData.tour.resort.name;
        }
        return this.searchData.resort.name;
    }

    @computed
    get hotelName(): string | null {
        return this.actualizationData && this.actualizationData.tour
            ? this.actualizationData.tour.hotel.name
            : null;
    }

    @computed
    get hotelCategoryName(): string | null {
        return this.actualizationData && this.actualizationData.tour
            ? this.actualizationData.tour.hotel.starsName
            : null;
    }

    @computed
    get operatorName(): string {
        return this.searchData.operator.name;
    }

    @computed
    get operatorId(): number {
        return this.searchData.sourceId;
    }

    @computed
    get operatorBookPageUrl(): string | null {
        return (this.actualizationData && this.actualizationData.tour)
            ? this.actualizationData.tour.tourOperatorTourPageUrl
            : null;
    }

    @computed
    get operatorInfoPageAlias(): string | null {
        return (this.actualizationData && this.actualizationData.tour)
            ? this.actualizationData.tour.tourOperatorInfoPageAlias
            : null;
    }

    @computed
    get mealName(): string {
        const linkedMealName = this.mealTypes.filter(m => m.code === this.searchData.hotel.mealName)[0];
        return linkedMealName && linkedMealName.altName || this.searchData.hotel.mealName;
    }

    @computed
    get mealCode(): string {
        return this.searchData.hotel.mealName;
    }

    @computed
    get accommodationCode(): string {
        return this.searchData.hotel.accommodation;
    }

    @computed
    get accommodationName(): string {
        return this.searchData.hotel.accommodationDescription;
    }

    @computed
    get systemRoomTypeId(): number {
        return this.searchData.hotel.systemRoomTypeId;
    }

    @computed
    get roomType(): string {
        return this.searchData.linkedRoomTypeName;
    }

    /**
     * У неслинкованных номеров systemRoomTypeId по дефолту выставлен в значение 0
     */
    @computed
    get isLinkedRoom(): boolean {
        return this.searchData.hotel.systemRoomTypeId !== DEFAULT_UNLINKED_ROOM_ID;
    }

    /**
     * Актуальная цена, которая в большинстве случаев должна выводиться в UI
     *
     * всегда полная цена (57 поле) имеет большие приоритет, однако если детальная актуализация прошла,
     * то показываем цену из актуализации (19 поле), т.к. она пришла от плагин хостов (от ТО)
     */
    @computed
    get actualPrice(): number {
        if (this.detailedActualizationData && this.detailedActualizationData.tour) {
            return this.detailedActualizationData.tour.price || this.previousPrice;
        }
        return this.previousPrice;
    }

    /**
     * Цена до проведения детальной актуализации, то есть из нашей БД,
     * когда ещё ядро не сходило в плагин хосты
     *
     * Может быть полной (со всякими сборами) или нет - это не важно
     */
    @computed
    get previousPrice(): number {
        if (this.primaryActualizationData && this.primaryActualizationData.tour) {
            return this.primaryActualizationData.tour.fullPrice || this.primaryActualizationData.tour.price;
        }
        return this.searchData.fullPrice || this.searchData.price;
    }

    /**
     * это НЕ оригинальная валюта тура от ТО! это валюта, в которой мы запрашивали актуализацию
     * как правило, плагинхосты конвертируют их по курсу ТО!
     *
     * на момент 31.08.2018 мы в КО всегда запрашиваем актуализацию в рублях!
     * см. параметры методов primaryActualize и detailedActualize
     */
    @computed
    get actualizationCurrencyId(): CurrencyId {
        const defaultCurrencyId = CurrencyId.RUB;
        if (!this.actualizationData || !this.actualizationData.tour) {
            return defaultCurrencyId;
        }
        return this.actualizationData.tour.actualizationCurrencyId;
    }

    @computed
    get actualizationCurrencyAlias(): string {
        if (!this.actualizationData || !this.actualizationData.tour) {
            return this.searchData.currency;
        }
        return this.actualizationData.tour.currency;
    }

    @computed
    get convertedPreviousPrice(): number {
        if (!this.currencyRates.isEmpty() && this.previousPrice) {
            return this.currencyRates.convertCurrency(this.previousPrice, this.actualizationCurrencyId, this.selectedCurrencyId);
        }
        return this.previousPrice;
    }

    @computed
    get convertedActualPrice(): number {
        if (!this.currencyRates.isEmpty() && this.actualPrice) {
            return this.currencyRates.convertCurrency(this.actualPrice, this.actualizationCurrencyId, this.selectedCurrencyId);
        }
        return this.actualPrice;
    }

    @computed
    get includedServices(): TourService[] {
        if (!this.actualizationData || !this.actualizationData.services) {
            return [];
        }
        return this.actualizationData.services.included;
    }

    @computed
    get notIncludedServices(): TourService[] {
        if (!this.actualizationData || !this.actualizationData.services) {
            return [];
        }
        return this.actualizationData.services.notIncluded;
    }


    @computed
    private get actualizationData(): ActualizationData | null {
        return this.detailedActualizationData || this.primaryActualizationData;
    }

    @computed
    get sletatTourId(): number | null {
        if (!this.actualizationData || !this.actualizationData.tour) {
            return null;
        }
        return this.actualizationData.tour.sletatTourId;
    }

    @computed
    private get isQuickBooking(): boolean {
        const tour = this.actualizationData ? this.actualizationData.tour : null;

        if (!tour || !this.isDetailedActualizationSuccess) {
            return false;
        }

        const hasPlacesInHotel = tour.isHotelInStopExtendedInfo === HotelInStop.hasPlaces;
        return this.hasExactDepartTickets && this.hasExactReturnTickets && hasPlacesInHotel;
    }

    @computed
    private get hasExactDepartTickets(): boolean {
        if (!this.actualizationData || !this.actualizationData.tour) {
            return false;
        }
        return hasDepartTickets(this.actualizationData.tour);
    }

    @computed
    private get hasExactReturnTickets(): boolean {
        if (!this.actualizationData || !this.actualizationData.tour) {
            return false;
        }
        return hasReturnTickets(this.actualizationData.tour);
    }

    @computed
    private get isSold(): boolean {
        return isTourSold(this.actualizationData && this.actualizationData.tour);
    }

    @computed
    private get isDepartDateInPast(): boolean {
        return !!this.departDate && (startOfDay(new Date()) > startOfDay(this.departDate));
    }

    isAvailableForOnlineBilling(availableOperatorsIds: number[]): boolean {
        return availableOperatorsIds.includes(this.operatorId);
    }

    primaryActualize(): Promise<ActualizationInfo> {
        const requestId = this.requestId;
        const offerId = this.offerId;
        const sourceId = this.sourceId;

        const params = {
            requestId: requestId,
            sourceId: sourceId,
            offerId: offerId,
            currencyAlias: CURRENCY_ALIAS
        };
        hotelPageLogger.info(`starting primary actualization for tour ${this.tourId}`);

        this.setPrimaryActualizationStatus(ActualizationStatus.InProcess);
        return primaryActualization(params, this.tourId)
            .then(resp => {
                this.setPrimaryActualizationStatus(resp.status);
                if (resp.data) {
                    this.setPrimaryActualizationData(resp.data);
                }
                hotelPageLogger.info(`primary actualization completed for tour ${this.tourId}`);
                return resp;
            });
    }

    actualizeCurrencyRates(): Promise<void> {
        return getCurrencyRates(this.sourceId, this.requestId)
            .then(rates => {
                runInAction(() => this.currencyRates = new CurrencyStore(rates));
                return;
            })
            .catch(() => {/**/ });
    }

    async detailedActualize() {
        if (!this.actualizationData) {
            this.setDetailedActualizationStatus(ActualizationStatus.Failed);
            hotelPageLogger.warn(`detailed actualization for tour ${this.tourId} isn't available`);
            return;
        }

        if (this.primaryActualizationStatus === ActualizationStatus.TourNotFound) {
            this.setDetailedActualizationStatus(ActualizationStatus.TourNotFound);
            hotelPageLogger.warn(`impossible detailed actualize tour ${this.tourId} that isn't found`);
            return;
        }

        if (!this.actualizationData.isDetailedExists) {
            this.setDetailedActualizationStatus(ActualizationStatus.Failed);
            hotelPageLogger.warn(`detailed actualization for tour ${this.tourId} not exists`);
            return;
        }

        if (!this.hotelId) {
            this.setDetailedActualizationStatus(ActualizationStatus.Failed);
            hotelPageLogger.warn(`hotel for tour ${this.tourId} isn't linked!`);
            return;
        }

        hotelPageLogger.info(`starting detailed actualization for tour ${this.tourId}`);

        this.setDetailedActualizationStatus(ActualizationStatus.InProcess);
        this.detailedService = detailedActualizationService({
            requestId: this.searchData.requestId,
            offerId: this.searchData.offerId,
            sourceId: this.searchData.sourceId,
            userId: '',
            currencyAlias: CURRENCY_ALIAS
        }, this.tourId);

        try {
            hotelPageLogger.info(`detailed actualization for tour ${this.tourId} completed`);
            const actualization = await this.detailedService.getPromise();

            if (actualization.status === ActualizationStatus.Failed) {
                this.setDetailedActualizationStatus(ActualizationStatus.Failed);
            } else if (actualization.status === ActualizationStatus.Success && actualization.data) {
                this.setDetailedActualizationData(actualization.data);
                this.setDetailedActualizationStatus(ActualizationStatus.Success);
            }
        } catch (err) {
            hotelPageLogger.info(`detailed actualization for tour ${this.tourId} cancelled`);
        }
    }

    stopDetailedActualization(): void {
        if (this.detailedService) {
            this.detailedService.cancel();
        }
    }

    @action
    markTourAsDuplicate(): void {
        this.isDuplicate = true;
    }

    @action
    setTourAddedToBasket(): void {
        this.isAddedToBasket = true;
    }

    @action
    changeSelectedCurrencyId(currencyId: CurrencyId): void {
        this.selectedCurrencyId = currencyId;
    }


    @action
    private setPrimaryActualizationData(actualization: ActualizationData): void {
        // selkin: очень редко, что валюта, в которой удалось актуализировать, отличается от той, в которой мы актуализацию запрашивали
        // однако, как говорил Демидов Олег, (плагины) такое возможно
        // поэтому перестраховываемся
        if (actualization.tour) {
            this.selectedCurrencyId = CurrencyId[actualization.tour.currency as keyof typeof CurrencyId];
        }
        this.primaryActualizationData = actualization;
        this.setFlightsInfo(actualization);
    }

    @action
    private setDetailedActualizationData(actualization: ActualizationData): void {
        // selkin: очень редко, что валюта, в которой удалось актуализировать, отличается от той, в которой мы актуализацию запрашивали
        // однако, как говорил Демидов Олег, (плагины) такое возможно
        // поэтому перестраховываемся
        if (actualization.tour) {
            this.selectedCurrencyId = CurrencyId[actualization.tour.currency as keyof typeof CurrencyId];
        }
        this.detailedActualizationData = actualization;
        this.setFlightsInfo(actualization);
    }

    private setFlightsInfo(actualization: ActualizationData): void {
        const flightsInfo = parseFlights({
            resources: actualization.resources,
            resourceData: actualization.resourceData,
            actualizedTour: actualization.tour
        }, this.tourId);

        runInAction(() => {
            this._flightsInfo = flightsInfo;
        });
    }

    @action
    private setDetailedActualizationStatus(status: ActualizationStatus): void {
        this.detailedActualizationStatus = status;
    }

    @action
    private setPrimaryActualizationStatus(status: ActualizationStatus): void {
        this.primaryActualizationStatus = status;
    }
}
